import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Divider, Flex, Grid, HStack, Modal, ModalBody, ModalContent, ModalOverlay, Spinner, Text, } from "@chakra-ui/react";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { usePubEventsByDate } from "../../hooks/usePubEvents";
import { useSmartBookingOffer } from "../../hooks/useSmartBookingOffer";
import { isTimeSlotWithinBuffer } from "../../helpers/bookingAvailability";
import DateSelectionCalendar from "./DateSelectionCalendar";
const SLOT_INTERVAL_MINUTES = 60;
const SLOT_LOOKAHEAD_HOURS = 48;
const PERIOD_META = [
    { key: "breakfast", label: "Breakfast" },
    { key: "lunch", label: "Lunch" },
    { key: "dinner", label: "Dinner" },
];
const bucketHour = (h) => {
    if (h >= 24)
        return "dinner";
    if (h < 12)
        return "breakfast";
    if (h < 17)
        return "lunch";
    return "dinner";
};
const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};
const formatSlotLabel = (hour, minute) => {
    const normalizedHour = hour >= 24 ? hour - 24 : hour;
    const displayHourFinal = normalizedHour % 12 === 0 ? 12 : normalizedHour % 12;
    return `${String(displayHourFinal).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};
const formatHourShort = (hour) => {
    const norm = hour >= 24 ? hour - 24 : hour;
    const display = norm % 12 === 0 ? 12 : norm % 12;
    const ampm = hour < 12 || hour >= 24 ? "AM" : "PM";
    return `${display}${ampm}`;
};
const formatPeriodRangeLabel = (slots) => {
    if (slots.length === 0)
        return "";
    const first = slots[0].hour;
    const last = slots[slots.length - 1].hour;
    return `${formatHourShort(first)} - ${formatHourShort(last + 1)}`;
};
const formatDayName = (date) => date.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
const formatMonth = (date) => date.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
const parseSlotTime = (time) => {
    if (!time)
        return null;
    if (Array.isArray(time)) {
        const [y, m, d, h, min] = time;
        return new Date(y, m - 1, d, h, min);
    }
    if (typeof time === "string") {
        const d = new Date(time);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
};
// Figma design tokens
const FG = {
    cardBg: "#15110C",
    border: "#34281B",
    activeGradient: "linear-gradient(144.86deg, #B7884B 0%, #9E5900 101.33%)",
    activeSlotBg: "#B7884B4D",
    activeSlotBorder: "#B7884B",
    counterOuterBorder: "#4E3D28",
    counterAccentBorder: "#E0B070",
    perkHeadline: "#E0B070",
    perkSub: "#F5EDE0",
    ctaGradient: "linear-gradient(90deg, #DEAE6E 0%, #AE7F43 100%)",
};
const ReserveYourExperienceForm = ({ pubId, dayEventUuid, onNavigate, pubColors, }) => {
    var _a, _b;
    const defaultColors = {
        bg: "#111111",
        text: "#FFFFFF",
        primary: "#C9A26A",
        secondary: "#A383FF",
        card_bg: "#1A1A1A",
        card_border: "#2B2B2B",
        icon_color: "white",
    };
    const colors = { ...defaultColors, ...pubColors };
    // ----- date carousel -----
    const today = useRef((() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    })()).current;
    const dates = useMemo(() => {
        const result = [];
        for (let i = 0; i < 30; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() + i);
            result.push(d);
        }
        return result;
    }, [today]);
    const [selectedDate, setSelectedDate] = useState(today);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [activePeriod, setActivePeriod] = useState("lunch");
    const [maleCount, setMaleCount] = useState(0);
    const [femaleCount, setFemaleCount] = useState(0);
    const [bookingMode, setBookingMode] = useState("standard");
    const [isAllDatesOpen, setIsAllDatesOpen] = useState(false);
    const isToday = (d) => d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate();
    const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();
    const selectedDateString = formatDateForAPI(selectedDate);
    const { data: eventsData, isLoading } = usePubEventsByDate(pubId, selectedDateString);
    const allEvents = useMemo(() => {
        var _a;
        const arr = (_a = eventsData === null || eventsData === void 0 ? void 0 : eventsData.data) === null || _a === void 0 ? void 0 : _a.events;
        return Array.isArray(arr) ? arr : [];
    }, [eventsData]);
    const dayEvent = useMemo(() => { var _a; return (_a = allEvents.find((e) => e.is_day_event === true)) !== null && _a !== void 0 ? _a : null; }, [allEvents]);
    const getEventSlots = (event) => {
        const directSlots = Array.isArray(event === null || event === void 0 ? void 0 : event.direct_slots)
            ? event.direct_slots
            : Array.isArray(event === null || event === void 0 ? void 0 : event.slots)
                ? event.slots
                : [];
        const subEventSlots = Array.isArray(event === null || event === void 0 ? void 0 : event.sub_events)
            ? event.sub_events.flatMap((s) => Array.isArray(s.slots) ? s.slots : [])
            : [];
        return [...directSlots, ...subEventSlots];
    };
    const applyTimeToSelectedDate = (source) => {
        const d = new Date(selectedDate);
        d.setHours(source.getHours(), source.getMinutes(), source.getSeconds(), source.getMilliseconds());
        return d;
    };
    const dayEventBookableWindows = useMemo(() => {
        if (!dayEvent)
            return [];
        const slots = getEventSlots(dayEvent);
        const bufferMinutes = (dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.buffer_time_minutes) || 0;
        const windows = [];
        for (const slot of slots) {
            let start = parseSlotTime(slot.start_time);
            let end = parseSlotTime(slot.end_time);
            if (!start || !end)
                continue;
            start = applyTimeToSelectedDate(start);
            end = applyTimeToSelectedDate(end);
            if (end <= start)
                end.setDate(end.getDate() + 1);
            if (!isTimeSlotWithinBuffer(start, bufferMinutes)) {
                const now = new Date();
                const minBookable = new Date(now.getTime() + bufferMinutes * 60 * 1000);
                if (minBookable >= end)
                    continue;
                start = minBookable;
            }
            start.setSeconds(0, 0);
            end.setSeconds(0, 0);
            windows.push({ start, end });
        }
        return windows;
    }, [dayEvent, selectedDate]);
    const slotsByPeriod = useMemo(() => {
        const result = {
            breakfast: [],
            lunch: [],
            dinner: [],
        };
        if (dayEventBookableWindows.length === 0)
            return result;
        const dayStart = new Date(selectedDate);
        dayStart.setHours(0, 0, 0, 0);
        for (let h = 0; h < SLOT_LOOKAHEAD_HOURS; h++) {
            for (let m = 0; m < 60; m += SLOT_INTERVAL_MINUTES) {
                const slotMoment = new Date(dayStart);
                slotMoment.setHours(h, m, 0, 0);
                const inWindow = dayEventBookableWindows.some((w) => slotMoment >= w.start && slotMoment < w.end);
                if (!inWindow)
                    continue;
                result[bucketHour(h)].push({
                    hour: h,
                    minute: m,
                    label: formatSlotLabel(h, m),
                });
            }
        }
        return result;
    }, [dayEventBookableWindows, selectedDate]);
    const visiblePeriods = PERIOD_META.filter((p) => slotsByPeriod[p.key].length > 0);
    useEffect(() => {
        if (visiblePeriods.length > 0 &&
            !visiblePeriods.find((p) => p.key === activePeriod)) {
            setActivePeriod(visiblePeriods[0].key);
        }
    }, [visiblePeriods, activePeriod]);
    const isSlotInPast = (slot) => {
        if (!isToday(selectedDate))
            return false;
        const now = new Date();
        return (slot.hour < now.getHours() ||
            (slot.hour === now.getHours() && slot.minute <= now.getMinutes()));
    };
    const reservationTimeISO = useMemo(() => {
        if (!selectedSlot)
            return undefined;
        const slotDate = new Date(selectedDate);
        if (selectedSlot.hour >= 24) {
            slotDate.setDate(slotDate.getDate() + 1);
            slotDate.setHours(selectedSlot.hour - 24, selectedSlot.minute, 0, 0);
        }
        else {
            slotDate.setHours(selectedSlot.hour, selectedSlot.minute, 0, 0);
        }
        return slotDate.toISOString();
    }, [selectedSlot, selectedDate]);
    const { data: smartOffer, isLoading: isSmartLoading } = useSmartBookingOffer({
        eventId: dayEventUuid,
        reservationTime: reservationTimeISO,
    });
    const smartPrice = (_a = smartOffer === null || smartOffer === void 0 ? void 0 : smartOffer.discounted_price) !== null && _a !== void 0 ? _a : 0;
    const smartListPrice = (_b = smartOffer === null || smartOffer === void 0 ? void 0 : smartOffer.list_price) !== null && _b !== void 0 ? _b : 0;
    const hasSmartOffer = !!smartOffer;
    const userPickedRef = useRef(false);
    useEffect(() => {
        if (userPickedRef.current)
            return;
        setBookingMode(hasSmartOffer ? "smart" : "standard");
    }, [hasSmartOffer]);
    const setBookingModeManual = (mode) => {
        userPickedRef.current = true;
        setBookingMode(mode);
    };
    useEffect(() => {
        if (selectedSlot)
            return;
        for (const p of PERIOD_META) {
            const firstAvailable = slotsByPeriod[p.key].find((s) => !isSlotInPast(s));
            if (firstAvailable) {
                setSelectedSlot(firstAvailable);
                if (p.key !== activePeriod)
                    setActivePeriod(p.key);
                return;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slotsByPeriod, selectedDate]);
    const getDayEventSlotId = () => {
        var _a;
        if (!dayEvent || !selectedSlot)
            return null;
        const slots = getEventSlots(dayEvent);
        if (!slots.length)
            return null;
        const target = new Date(selectedDate);
        if (selectedSlot.hour >= 24) {
            target.setDate(target.getDate() + 1);
            target.setHours(selectedSlot.hour - 24, selectedSlot.minute, 0, 0);
        }
        else {
            target.setHours(selectedSlot.hour, selectedSlot.minute, 0, 0);
        }
        for (const slot of slots) {
            const startRaw = parseSlotTime(slot.start_time);
            const endRaw = parseSlotTime(slot.end_time);
            if (!startRaw || !endRaw)
                continue;
            const slotStart = applyTimeToSelectedDate(startRaw);
            const slotEnd = applyTimeToSelectedDate(endRaw);
            if (slotEnd <= slotStart)
                slotEnd.setDate(slotEnd.getDate() + 1);
            if (target >= slotStart && target < slotEnd) {
                return typeof slot.id === "number" ? slot.id : null;
            }
        }
        const fallbackId = (_a = slots[0]) === null || _a === void 0 ? void 0 : _a.id;
        return typeof fallbackId === "number" ? fallbackId : null;
    };
    const totalPax = maleCount + femaleCount;
    const canReserve = !!selectedSlot && totalPax > 0 && !!dayEvent;
    const handleReserveNow = () => {
        var _a;
        if (!canReserve || !selectedSlot)
            return;
        const uuid = (dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.uuid) || dayEventUuid;
        const slotId = getDayEventSlotId();
        if (!uuid || !slotId)
            return;
        const hourForTimeStr = selectedSlot.hour >= 24 ? selectedSlot.hour - 24 : selectedSlot.hour;
        const timeStr = `${String(hourForTimeStr).padStart(2, "0")}:${String(selectedSlot.minute).padStart(2, "0")}`;
        const dateForQuery = new Date(selectedDate);
        if (selectedSlot.hour >= 24) {
            dateForQuery.setDate(dateForQuery.getDate() + 1);
        }
        const dateString = formatDateForAPI(dateForQuery);
        const formattedDateTime = `${dateString}T${timeStr}:00`;
        const params = new URLSearchParams({
            date: dateString,
            dayTime: timeStr,
            slot: String(slotId),
            time: formattedDateTime,
            "male-count": String(maleCount),
            "female-count": String(femaleCount),
        });
        if (bookingMode === "smart" && (smartOffer === null || smartOffer === void 0 ? void 0 : smartOffer.coupon_id)) {
            params.set("coupon_id", String(smartOffer.coupon_id));
        }
        if (bookingMode === "smart" && dayEvent) {
            params.set("smart_only", "1");
        }
        if ((dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.is_table_booking_enabled) &&
            !(dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.is_pass_booking_enabled)) {
            onNavigate(`/event/booking/${uuid}?tab=Tables&step=2&${params.toString()}`);
            return;
        }
        if ((dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.is_pass_booking_enabled) &&
            !(dayEvent === null || dayEvent === void 0 ? void 0 : dayEvent.is_table_booking_enabled)) {
            const slot = (_a = dayEvent.direct_slots) === null || _a === void 0 ? void 0 : _a[0];
            if (slot) {
                onNavigate(`/event/booking/${uuid}?direct_slot=${slot.name}_${slot.id}&${params.toString()}`);
                return;
            }
        }
        onNavigate(`/event/booking/pax/${uuid}?${params.toString()}`);
    };
    // ----- smart-booking perks -----
    const smartPerks = useMemo(() => {
        var _a, _b;
        const bundles = (_b = (_a = smartOffer === null || smartOffer === void 0 ? void 0 : smartOffer.bundle_definition) === null || _a === void 0 ? void 0 : _a.bundles) !== null && _b !== void 0 ? _b : [];
        return bundles.slice(0, 3).map((b) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
            if (((_a = b.kind) === null || _a === void 0 ? void 0 : _a.includes("BILL")) && b.discount) {
                return { headline: `${b.discount}%`, sub: "Off your bill" };
            }
            if ((_b = b.kind) === null || _b === void 0 ? void 0 : _b.includes("LOYALTY")) {
                const pts = (_f = (_c = b.points_earned) !== null && _c !== void 0 ? _c : (_e = (_d = b.loyalty_config) === null || _d === void 0 ? void 0 : _d.points_by_visit) === null || _e === void 0 ? void 0 : _e.this_visit) !== null && _f !== void 0 ? _f : (_h = (_g = b.loyalty) === null || _g === void 0 ? void 0 : _g.points_by_visit) === null || _h === void 0 ? void 0 : _h.this_visit;
                if (pts)
                    return { headline: `${pts}`, sub: "Loyalty points" };
            }
            if ((((_j = b.kind) === null || _j === void 0 ? void 0 : _j.includes("FOOD")) || ((_k = b.kind) === null || _k === void 0 ? void 0 : _k.includes("DRINK"))) &&
                ((_l = b.menu_items) === null || _l === void 0 ? void 0 : _l.length)) {
                const item = b.menu_items[0];
                return { headline: `+${item.quantity}`, sub: `Free ${item.name}` };
            }
            return { headline: (_m = b.name) !== null && _m !== void 0 ? _m : "Perk", sub: (_o = b.description) !== null && _o !== void 0 ? _o : "" };
        });
    }, [smartOffer]);
    const loyaltyBundleInfo = useMemo(() => {
        var _a, _b, _c, _d, _e;
        const bundles = (_b = (_a = smartOffer === null || smartOffer === void 0 ? void 0 : smartOffer.bundle_definition) === null || _a === void 0 ? void 0 : _a.bundles) !== null && _b !== void 0 ? _b : [];
        const lb = bundles.find((b) => { var _a; return (_a = b.kind) === null || _a === void 0 ? void 0 : _a.includes("LOYALTY"); });
        if (!lb)
            return null;
        const pbv = (_d = (_c = lb === null || lb === void 0 ? void 0 : lb.loyalty_config) === null || _c === void 0 ? void 0 : _c.points_by_visit) !== null && _d !== void 0 ? _d : (_e = lb === null || lb === void 0 ? void 0 : lb.loyalty) === null || _e === void 0 ? void 0 : _e.points_by_visit;
        if (!pbv)
            return null;
        const parts = [];
        if (pbv.this_visit)
            parts.push(`${pbv.this_visit} on this visit`);
        if (pbv.next_visit)
            parts.push(`${pbv.next_visit} on next visit`);
        if (pbv.next_to_next)
            parts.push(`${pbv.next_to_next} on later visit`);
        return parts.length ? parts.join(", ") + "." : null;
    }, [smartOffer]);
    // ----- render helpers -----
    const renderDateTile = (date, idx) => {
        const selected = isSameDay(date, selectedDate);
        return (_jsxs(Box, { flexShrink: 0, w: ["52px", "56px", "56px", "48px"], minW: ["52px", "56px", "56px", "48px"], h: ["56px", "58px", "58px", "50px"], borderRadius: "8px", background: selected ? FG.activeGradient : FG.cardBg, border: `1px solid ${selected ? "transparent" : FG.border}`, display: "flex", flexDir: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", py: ["8px", "8px", "8px", "6px"], onClick: () => {
                setSelectedDate(date);
                setSelectedSlot(null);
            }, children: [_jsx(Text, { fontSize: "10px", fontWeight: "400", color: "white", lineHeight: "12px", textAlign: "center", children: formatDayName(date) }), _jsx(Text, { fontSize: "14px", fontWeight: "700", color: "white", lineHeight: "18px", textAlign: "center", children: date.getDate() }), _jsx(Text, { fontSize: "10px", fontWeight: "400", color: "white", lineHeight: "12px", textAlign: "center", children: formatMonth(date) })] }, idx));
    };
    const renderTimeSlot = (slot) => {
        const isSel = (selectedSlot === null || selectedSlot === void 0 ? void 0 : selectedSlot.hour) === slot.hour && (selectedSlot === null || selectedSlot === void 0 ? void 0 : selectedSlot.minute) === slot.minute;
        const isPast = isSlotInPast(slot);
        return (_jsx(Box, { flexShrink: 0, w: ["56px", "60px"], minW: ["56px", "60px"], h: "30px", borderRadius: "8px", border: `1px solid ${isSel ? FG.activeSlotBorder : FG.border}`, bg: isSel ? FG.activeSlotBg : FG.cardBg, opacity: isPast ? 0.35 : 1, cursor: isPast ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", onClick: () => {
                if (!isPast)
                    setSelectedSlot(slot);
            }, children: _jsx(Text, { fontSize: "12px", fontWeight: "400", color: "white", lineHeight: "16px", textAlign: "center", children: slot.label }) }, `${slot.hour}-${slot.minute}`));
    };
    const Counter = ({ label, value, onDec, onInc, }) => (_jsxs(Flex, { flex: 1, align: "center", justify: "space-between", borderRadius: "8px", border: `1px solid ${FG.border}`, bg: FG.cardBg, boxShadow: "0px 4px 2px 0px #00000033", pt: "6px", pb: "8px", px: "14px", children: [_jsx(Text, { fontSize: "12px", fontWeight: "400", color: colors.text, children: label }), _jsxs(HStack, { spacing: 2, children: [_jsx(Box, { as: "button", w: "24px", h: "24px", borderRadius: "full", border: `0.8px solid ${FG.counterOuterBorder}`, display: "flex", alignItems: "center", justifyContent: "center", onClick: onDec, cursor: "pointer", _focus: { outline: "none" }, children: _jsx(Box, { w: "18px", h: "18px", borderRadius: "full", border: `1.28px solid ${FG.counterAccentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", children: _jsx(MinusIcon, { boxSize: "7px", color: FG.counterAccentBorder }) }) }), _jsx(Text, { fontFamily: "'Cinzel', serif", fontWeight: "700", fontSize: "12px", color: "white", lineHeight: "20px", w: "14px", textAlign: "center", children: value }), _jsx(Box, { as: "button", w: "24px", h: "24px", borderRadius: "full", border: `0.8px solid ${FG.counterOuterBorder}`, display: "flex", alignItems: "center", justifyContent: "center", onClick: onInc, cursor: "pointer", _focus: { outline: "none" }, children: _jsx(Box, { w: "18px", h: "18px", borderRadius: "full", border: `1.28px solid ${FG.counterAccentBorder}`, display: "flex", alignItems: "center", justifyContent: "center", children: _jsx(AddIcon, { boxSize: "7px", color: FG.counterAccentBorder }) }) })] })] }));
    const RadioDot = ({ selected }) => (_jsx(Box, { w: "20px", h: "20px", borderRadius: "full", border: `2px solid ${selected ? FG.activeSlotBorder : FG.counterOuterBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, children: selected && (_jsx(Box, { w: "10px", h: "10px", borderRadius: "full", background: FG.activeGradient })) }));
    // ----- view -----
    return (_jsxs(Box, { w: "100%", bg: colors.bg, border: `1px solid ${FG.border}`, borderRadius: "20px", p: [4, 5, 6, 4], mt: ["0px", "42px"], children: [_jsx(Text, { textAlign: "center", fontSize: "16px", fontWeight: "700", lineHeight: "20px", color: colors.text, textTransform: "uppercase", mb: [5, 5, 5, 3], children: "Reserve Your Experience" }), _jsx(Text, { fontSize: "12px", fontWeight: "400", lineHeight: "11.61px", color: colors.text, textTransform: "capitalize", mb: 3, children: "Date" }), _jsxs(Flex, { gap: 2, overflowX: "auto", overflowY: "hidden", pb: 2, mb: [5, 5, 5, 3], mx: [-4, -5, -6, -4], px: [4, 5, 6, 4], sx: {
                    "&::-webkit-scrollbar": { display: "none" },
                    scrollbarWidth: "none",
                }, children: [dates.map((d, i) => renderDateTile(d, i)), _jsx(Box, { flexShrink: 0, w: ["52px", "56px", "56px", "48px"], minW: ["52px", "56px", "56px", "48px"], h: ["56px", "58px", "58px", "50px"], borderRadius: "8px", bg: FG.cardBg, border: `1px solid ${FG.border}`, display: "flex", flexDir: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", py: ["8px", "8px", "8px", "6px"], onClick: () => setIsAllDatesOpen(true), children: _jsxs(Text, { fontSize: "10px", fontWeight: "400", color: colors.text, textAlign: "center", lineHeight: "1.3", children: ["View All", "\n", "Dates"] }) })] }), _jsx(Text, { fontSize: "12px", fontWeight: "400", lineHeight: "11.61px", color: colors.text, textTransform: "capitalize", mb: 3, children: "Time" }), _jsxs(Box, { borderRadius: "12px", border: `1px solid ${FG.border}`, p: "10px", mb: [5, 5, 5, 3], children: [_jsx(Box, { bg: "#000000", border: `1px solid ${FG.border}`, borderRadius: "30px", p: "2px", mb: 3, children: _jsx(Flex, { children: PERIOD_META.map((p) => {
                                const isActive = activePeriod === p.key;
                                const periodSlots = slotsByPeriod[p.key];
                                const isAvailable = periodSlots.length > 0;
                                const rangeLabel = formatPeriodRangeLabel(periodSlots);
                                return (_jsxs(Box, { flex: 1, pt: "6px", pr: "14px", pb: "8px", pl: "14px", borderRadius: "30px", background: isActive ? FG.activeGradient : "transparent", opacity: isAvailable ? 1 : 0.4, cursor: isAvailable ? "pointer" : "not-allowed", textAlign: "center", onClick: () => {
                                        if (isAvailable)
                                            setActivePeriod(p.key);
                                    }, children: [_jsx(Text, { fontSize: "12px", fontWeight: "400", lineHeight: "16px", color: "white", children: p.label }), rangeLabel && (_jsx(Text, { fontSize: "10px", fontWeight: "400", lineHeight: "12px", color: "white", textTransform: "uppercase", opacity: isActive ? 1 : 0.7, children: rangeLabel }))] }, p.key));
                            }) }) }), isLoading ? (_jsx(Flex, { justify: "center", py: 3, children: _jsx(Spinner, { size: "sm", color: FG.activeSlotBorder }) })) : slotsByPeriod[activePeriod].length === 0 ? (_jsx(Text, { fontSize: "xs", color: colors.text, opacity: 0.6, px: 2, py: 2, children: "No slots available for this period." })) : (_jsx(Flex, { gap: 2, overflowX: "auto", pt: 1, pb: 1, sx: {
                            "&::-webkit-scrollbar": { display: "none" },
                            scrollbarWidth: "none",
                        }, children: slotsByPeriod[activePeriod].map((s) => renderTimeSlot(s)) }))] }), _jsx(Text, { fontSize: "12px", fontWeight: "400", lineHeight: "11.61px", color: colors.text, textTransform: "capitalize", mb: 3, children: "Party Of" }), _jsxs(Flex, { gap: 3, mb: [5, 5, 5, 3], children: [_jsx(Counter, { label: "Male", value: maleCount, onDec: () => setMaleCount((v) => Math.max(0, v - 1)), onInc: () => setMaleCount((v) => v + 1) }), _jsx(Counter, { label: "Female", value: femaleCount, onDec: () => setFemaleCount((v) => Math.max(0, v - 1)), onInc: () => setFemaleCount((v) => v + 1) })] }), hasSmartOffer && (_jsxs(Box, { borderRadius: "16px", bg: FG.cardBg, border: `1px solid ${bookingMode === "smart" ? FG.activeSlotBorder : FG.border}`, p: [4, 4, 4, 3], mb: [3, 3, 3, 2], cursor: "pointer", onClick: () => setBookingModeManual("smart"), children: [_jsxs(Flex, { align: "flex-start", justify: "space-between", gap: 3, children: [_jsxs(Flex, { align: "flex-start", gap: 3, flex: 1, children: [_jsx(RadioDot, { selected: bookingMode === "smart" }), _jsxs(Box, { children: [_jsx(Text, { fontSize: "12px", fontWeight: "500", lineHeight: "16px", color: FG.perkHeadline, textTransform: "uppercase", mb: 1, children: "Smart Booking" }), _jsxs(Text, { fontSize: "12px", fontWeight: "400", lineHeight: "20px", color: colors.text, children: ["Experience", " ", _jsx(Text, { as: "span", fontStyle: "italic", color: FG.perkHeadline, children: "curated" }), ", just", " ", _jsx(Text, { as: "span", fontStyle: "italic", color: FG.perkHeadline, children: "for you" }), "."] })] })] }), _jsx(Text, { fontSize: "16px", fontWeight: "600", lineHeight: "20px", color: colors.text, whiteSpace: "nowrap", children: isSmartLoading ? "..." : `₹ ${smartPrice}` })] }), smartPerks.length > 0 && (_jsxs(_Fragment, { children: [_jsx(Divider, { borderColor: FG.border, my: 3 }), _jsx(Grid, { templateColumns: `repeat(${smartPerks.length}, 1fr)`, gap: 2, children: smartPerks.map((p, i) => (_jsxs(Box, { borderRadius: "10px", border: `1px solid ${FG.border}`, bg: FG.cardBg, py: 2, px: 2, textAlign: "center", children: [_jsx(Text, { fontFamily: "'Cinzel', serif", fontWeight: "400", fontSize: "20px", lineHeight: "20px", color: FG.perkHeadline, textAlign: "center", children: p.headline }), _jsx(Text, { fontStyle: "italic", fontSize: "10px", lineHeight: "14px", color: FG.perkSub, textAlign: "center", children: p.sub })] }, i))) }), loyaltyBundleInfo && (_jsxs(Box, { mt: 3, children: [_jsx(Text, { fontSize: "10px", fontWeight: "400", lineHeight: "14px", color: colors.text, children: "\u2022 Redeem Loyalty points against the bill directly." }), _jsx(Text, { fontSize: "10px", fontWeight: "400", lineHeight: "14px", color: colors.text, opacity: 0.7, mt: 1, children: loyaltyBundleInfo })] }))] }))] })), _jsx(Box, { borderRadius: "16px", bg: FG.cardBg, border: `1px solid ${bookingMode === "standard" ? FG.activeSlotBorder : FG.border}`, p: [4, 4, 4, 3], mb: [5, 5, 5, 3], cursor: "pointer", onClick: () => setBookingModeManual("standard"), children: _jsxs(Flex, { align: "center", justify: "space-between", gap: 3, children: [_jsxs(Flex, { align: "center", gap: 3, children: [_jsx(RadioDot, { selected: bookingMode === "standard" }), _jsx(Text, { fontSize: "12px", fontWeight: "500", lineHeight: "16px", color: colors.text, textTransform: "uppercase", children: "Standard Booking" })] }), _jsx(Text, { fontSize: "12px", fontWeight: "600", color: colors.text, children: "Free" })] }) }), _jsx(Button, { w: "100%", h: "52px", borderRadius: "14px", background: FG.ctaGradient, color: "#000000", fontFamily: "'Baskervville', serif", fontWeight: "500", fontStyle: "italic", fontSize: "14px", lineHeight: "20px", isDisabled: !canReserve, onClick: handleReserveNow, _hover: { opacity: 0.9 }, _disabled: { opacity: 0.5, cursor: "not-allowed" }, _active: { opacity: 0.85 }, children: _jsxs(Flex, { align: "center", gap: 2, children: [_jsx(Text, { fontFamily: "'Baskervville', serif", fontWeight: "500", fontStyle: "italic", fontSize: "14px", lineHeight: "20px", children: "Reserve Now" }), _jsx(Text, { fontStyle: "normal", fontSize: "16px", lineHeight: "1", mt: "1px", children: "\u2192" })] }) }), _jsxs(Modal, { isOpen: isAllDatesOpen, onClose: () => setIsAllDatesOpen(false), isCentered: true, size: "md", children: [_jsx(ModalOverlay, {}), _jsx(ModalContent, { bg: colors.bg, border: `1px solid ${FG.border}`, children: _jsx(ModalBody, { p: 4, children: _jsx(DateSelectionCalendar, { selectedDate: selectedDate, onDateChange: (d) => {
                                    if (d)
                                        setSelectedDate(d);
                                }, onContinue: () => {
                                    setSelectedSlot(null);
                                    setIsAllDatesOpen(false);
                                }, pubColors: {
                                    primary: colors.primary,
                                    secondary: colors.secondary,
                                    text: colors.text,
                                    bg: colors.bg,
                                    card_border: FG.border,
                                } }) }) })] })] }));
};
export default ReserveYourExperienceForm;
